import { CrossWindowClientProxy, CrossWindowEvents, CrossWindowProxy, CrossWindowSocketProxy, WebSocketEventTypes } from './crossWindowProxy';
import { RcLoggerService } from '../remote-control-ng/services/rcLoggerService';
import { IMessageHandler, CrossWindowMessage } from './definitions';

export interface IWebSocketProvider {
    getSocket(host: string, protocol: string): WebSocket;
}

export class DefaultWebSocketProvider implements IWebSocketProvider {
    public getSocket = (host: string, protocol: string): WebSocket => {
        const socketImpl = window.WebSocket || (<any>window).MozWebSocket;
        return protocol ? new socketImpl(host, protocol) : new socketImpl(host);
    };
}

enum CLOSED_REASONS {
    CONNECTING = 0,
    OPEN,
    CLOSING
}

// const WebSocketCodes: Record<number, string> = {
//     1000: 'WebSocket closed normally.',
//     1001: 'WebSocket closed because server is shutting down.',
//     1002: 'WebSocket closed because of a protocol error.',
//     1003: 'WebSocket closed because the server received abnormal data.',
//     1005: 'WebSocket closed without sending a status code.',
//     1006: 'WebSocket closed without sending a close frame.',
//     1007: 'WebSocket closed because the server received abnormal data.',
//     1008: 'WebSocket closed because the server received abnormal data.',
//     1009: 'WebSocket closed because the server received a data frame that was too large.',
//     1010: 'WebSocket closed because the server failed to negotiate an extension.',
//     1011: 'WebSocket closed because the server encountered an unexpected condition.',
//     1015: 'WebSocket closed because the TLS handshake was unsuccessful.'
// }

export abstract class BaseSocket {
    protected _messageHandler: IMessageHandler;
    public window: Window;
    public targetWindow: Window;

    constructor() {
        this.window = window;
    }

    public get messageHandler(): IMessageHandler {
        throw new Error('Getter not implemented.');
    }

    public set messageHandler(messageHandler: IMessageHandler) {
        this._messageHandler = messageHandler;
    }
    // sourceProxy: CrossWindowSocketProxy;
    // receiverProxy: CrossWindowClientProxy;
    // /** create a new proxy for the remote socket running in a separate window
    //  * and start listening for events and messages from that socket */
    // registerSourceProxy(): void;
    // /** stop listening for events and messages from the remote socket and delete the proxy */
    // unregisterSourceProxy(): void;
    // /** create a new proxy for the socketless window and create observables for
    //  *  socket requests coming from that socketless window */
    // registerReceiverProxy(window: Window): void;
    // /** stop listening to requests from the socketless window and delete the proxy*/
    // unregisterReceiverProxy(): void;

    public abstract connect(host: string, protocol: string, secured: boolean): void;
    public abstract send(data: Uint8Array): void;
    public abstract close(code?: number, reason?: string): void;
    public abstract isConnected(): boolean;
    public abstract attach(socket: WebSocket | BaseSocket): void;
    public abstract onClose(event: CloseEvent): void;
}

export class SotiWebSocket extends BaseSocket {
    private _webSocketProvider: IWebSocketProvider;
    private _socket: WebSocket;

    // Source window (the window on which the WebSocket was created)
    private _sourceProxy: CrossWindowSocketProxy;

    // Target window for socket messages (optional - used for RC popout window)
    // NOTE: This will need to be cleared when re-docking RC session from popout window into original window is supported
    // private _targetProxy: CrossWindowClientProxy;
    private _unsubscribe: () => void;

    constructor(private mcrcLog: RcLoggerService, provider?: IWebSocketProvider) {
        super()
        this._webSocketProvider = provider || new DefaultWebSocketProvider();
    }

    public registerSocketProxy() {
        this._sourceProxy = new CrossWindowSocketProxy(this.window);
        this._unsubscribe = this._sourceProxy.onMessageForwarded((msg: CrossWindowMessage) => {
            // console.debug(`Receiving postMessage in registerSocketProxy ${msg?.type} on ${this._sourceProxy.window?.location.href}`, msg);
            if (msg?.type as string === WebSocketEventTypes.RC_SEND) {
                this.send(msg.data);
            }
        });
    }

    public unregisterSocketProxy() {
        this._unsubscribe?.();
        this._sourceProxy = null;
    }

    public get socketProxy(): CrossWindowSocketProxy { return this._sourceProxy; }

    public override connect = (host: string, protocol: string, secured: boolean) => {
        // this.mcrcLog.debug('WebSocket.connect = host:' + host + ' protocol:' + protocol + ' secured:' + secured);
        const hostString = this.getHostString(host, secured);
        this._socket = this._webSocketProvider.getSocket(hostString, protocol);

        if (this._socket) {
            this._socket.binaryType = 'arraybuffer';

            this._socket.onmessage = this.onMessage;
            this._socket.onopen = this.onConnect;
            this._socket.onclose = this.onClose;
            this._socket.onerror = this.onError;
        } else {
            // this.mcrcLog.error("WebSockets not supported");
            let error = new Error("WebSockets not supported");
            error.name = "NotSupportedError";
            throw error;
        }
    };

    public override send = (data: Uint8Array) => {
        if (!this.isConnected()) return;
        this._socket.send(data);
    };

    public override close = (code?: number, reason?: string) => this._socket.close(code, reason);

    public override isConnected = () => (this._socket.readyState === CLOSED_REASONS.OPEN);

    // video streaming - attach web socket (to DS) for JS code using mcrc
    public override attach = (socket: WebSocket | BaseSocket) => {
        this._socket = socket as WebSocket;
        this._socket.onmessage = this.onMessage;
    };

    private getHostString = (host: string, secured: boolean): string => {
        const schema = (secured) ? 'wss://' : 'ws://';
        return schema + host;
    };

    protected onError = (event: Event) => {
        // this.mcrcLog.error('WebSocket.socket.onerror: ' + JSON.stringify(event));
        if (this._messageHandler) {
            this._messageHandler.onError(event);
        }
        // If there is a popup window, let the window know about the socket event
        if (this.targetWindow) CrossWindowProxy.postMessage(this.targetWindow, CrossWindowEvents.RC_SOCKET_EVENT, undefined, { type: WebSocketEventTypes.SOCKET_ERROR });
    };

    protected onMessage = (event: MessageEvent<any>) => {
        if (this.targetWindow) {
            // Transfer the message data to the target window so it can be accessed from there
            CrossWindowProxy.postMessage(this.targetWindow, CrossWindowEvents.RC_FORWARD_DATA, event.data);
        }
        else if (this._messageHandler) this._messageHandler.onMessage(event);
    };

    protected onConnect = (event: Event) => {
        // this.mcrcLog.debug('WebSocket.socket.onopen: ' + JSON.stringify(event));
        if (this._messageHandler) {
            this._messageHandler.onConnect(event);
        }
        // If there is a popup window, let the window know about the socket event
        if (this.targetWindow) CrossWindowProxy.postMessage(this.targetWindow, CrossWindowEvents.RC_SOCKET_EVENT, undefined, { type: WebSocketEventTypes.SOCKET_CONNECT });
    };

    public onClose = (event: CloseEvent) => {
        // this.mcrcLog.debug('WebSocket.socket.onopen: ' + JSON.stringify(event));
        if (this._messageHandler) {
            this._messageHandler.onClose(event);
        }
        // If there is a popup window, let the window know about the socket event
        if (this.targetWindow) CrossWindowProxy.postMessage(this.targetWindow, CrossWindowEvents.RC_SOCKET_EVENT, undefined, { type: WebSocketEventTypes.SOCKET_CLOSE });
    };
}

/** SocketSurrogate is the class that uses the websocket in teh remote tab to send and receive websocket messages,
 *  used in the socket-less popout instance
    */
export class SocketSurrogate extends BaseSocket {
    private unbindRcMessage;

    constructor() {
        super();
    }

    connect(host: string, protocol: string, secured: boolean): void {
        throw new Error('Method not implemented.');
    }
    send(data: Uint8Array): void {
        // Post message to socket when it's targeting a different window (i.e. popout)
        CrossWindowProxy.postMessage(this.targetWindow, WebSocketEventTypes.RC_SEND, data.buffer);
    }
    close(code?: number, reason?: string): void {
        throw new Error('Method not implemented.');
    }
    isConnected = (): boolean => !!this.targetWindow;

    attach(socket: WebSocket | BaseSocket): void {
        this.targetWindow = (socket as BaseSocket).window;
        if (this.unbindRcMessage) {
            this.unbindRcMessage();
        }
        this.unbindRcMessage = CrossWindowProxy.onWindowMessage(this.window, (msg: CrossWindowMessage) => {
            // console.debug(`Receiving postMessage in attach ${msg?.type} on ${this.window.location.href}`, msg);
            if (msg.type === CrossWindowEvents.RC_FORWARD_DATA) {
                this._messageHandler.onMessage(<any>msg as Event);
            } else if (msg.type === CrossWindowEvents.RC_SOCKET_EVENT) {
                switch (msg.data.type) {
                    case WebSocketEventTypes.SOCKET_CLOSE:
                        this._messageHandler.onClose(new CloseEvent(WebSocketEventTypes.SOCKET_CLOSE));
                        break;
                    case WebSocketEventTypes.SOCKET_CONNECT:
                        this._messageHandler.onConnect(new Event(WebSocketEventTypes.SOCKET_CONNECT));
                        break;
                    case WebSocketEventTypes.SOCKET_ERROR:
                        this._messageHandler.onError(new ErrorEvent(WebSocketEventTypes.SOCKET_ERROR));
                        break;
                }
            }
        });
    }

    protected onError = (event: Event) => {
        // this.mcrcLog.error('WebSocket.socket.onerror: ' + JSON.stringify(event));
        this._messageHandler?.onError(event);
    };

    protected onMessage = (event: MessageEvent<any>) => {
        this._messageHandler?.onMessage(event);
    };

    protected onConnect = (event: Event) => {
        // this.mcrcLog.debug('WebSocket.socket.onopen: ' + JSON.stringify(event));
        this._messageHandler?.onConnect(event);
    };

    public onClose = (event: CloseEvent) => {
        // this.mcrcLog.debug('WebSocket.socket.onopen: ' + JSON.stringify(event));
        this._messageHandler?.onClose(event);
        if (this.unbindRcMessage) {
            this.unbindRcMessage();
        }
    };
}
